// src/com/arrive_info/ArriveInfoUploader.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    DialogContent, Button, DialogActions
} from '@mui/material';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // upload file
import DoneIcon from '@mui/icons-material/Done'; // close the dialog.
import FindInPageIcon from '@mui/icons-material/FindInPage'; // search file
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant'; // error
import { useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { AppWord, AppStyle } from '../../value/AppConst';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import DraggableEditor from '../../common/DraggableEditor';
import FlexyTable from '../../common/flexytable/FlexyTable';
import VzmCheckbox from '../../common/VzmCheckbox';
import { authTableTreeRepo, userInfoRepo } from '../../value/AppModel';
import { usePapaParse } from 'react-papaparse';
import { ArriveInfoDictionary } from './ArriveInfoDict';
import { UPLOAD_ARRIVE_INFO } from './ArriveInfoGql'; // 여기서 올리고, 여기서 반응을 보여줘야 함.
import IsValid from '../../value/IsValid';
import InputUtil from '../../value/InputUtil';

const HeaderLine = styled(Box)({
    display: 'flex', alignItems: 'center', padding: 10, boxSizing: 'border-box',
});

const tableProps = ArriveInfoDictionary.uploadTableProps;
// extract for upload from InputMap
const { dcCd, storeId, basicDockno, distanceOne, distanceRound, basicExpectRunTime, startReqTimeToday, arriveReqTimeNext } = ArriveInfoDictionary.inputMap;
const uploadMap = {
    dcCd, storeId, basicDockno, distanceOne, distanceRound,
    basicExpectRunTime, startReqTimeToday, arriveReqTimeNext
};
const EmptyCsvData = {data:[], errors:[], meta:{}};

function checkItem(item) {
    const errors = [];
    for(const id of Object.keys(uploadMap)) {
        const label = uploadMap[id].label;
        if(IsValid.nullString(item[id])) {
            if(uploadMap[id].required) {
                errors.push(`${label} 없음.`);
            }
        }
        else {
            const check = uploadMap[id].check;
            if(check) {
                const good = check(item[id]);
                if(!good) {
                    errors.push(`${label} 오류.`);
                }
            }
        }
    }
    if(errors.length > 0) {
        item.ok = false;
        item.message = errors.join("\n");
    }
    else {
        item.ok = true;
        item.message = 'Ready';
    }
    return item;
}

function checkAndMap(dcCd, data) {
    let countError = 0;
    const records = data.map((array) => {
        let item = {
            dcCd: array[0],
            storeId: array[1],
            basicDockno: array[2],
            distanceOne: array[3],
            distanceRound: array[4],
            basicExpectRunTime: array[5],
            startReqTimeToday: array[6],
            arriveReqTimeNext: array[7],
        }
        if(item.dcCd !== dcCd) {
            item.ok = false;
            item.message = 'DC 불일치';
        }
        else item = checkItem(item);
        if(!item.ok) countError++;
        return item;
    });
    return {records, countError};
}

export default function ArriveInfoUploader({
    open,
    csvText,
    onClose,
}) {
    const [text, setText] = useState('');
    const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [csvData, setCsvData] = useState(EmptyCsvData); // {data:[], errors:[], meta:{}}
    const [tableData, setTableData] = useState([]);
    const [errorCount, setErrorCount] = useState(0);
    const [changed, setChanged] = useState(false);
    const [msgDone, setMsgDone] = useState('');
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const checkAuth = useAuthorizationCheck();
    const [useEucKr, setUseEucKr] = useState(false);
    const fileInputRef = useRef(null);
    const { readString } = usePapaParse();
    const wellUploadCountRef = useRef(0);

    const [uploadArriveInfo, responseUpload] = useMutation(UPLOAD_ARRIVE_INFO, {
        onCompleted: (data, option) => {onCompletedUpload(data, option);},
        onError: (error) => {
            addAlert({title: "도착정보 업로드 오류", message: [error.message]});
        }
    });

    useEffect(()=>{
        if(open) {
            setText(csvText);
        }
        else {
            setText('');
            setCsvData(EmptyCsvData);
        }
    }, [open, csvText]);

    // 내부에서 다시 파일 선택할 수 있으므로 csvText에서 직접 처리하지 않고 csvText => text => csvData로 처리.
    useEffect(()=>{
        if(!IsValid.nullString(text)) {
            readString(text, {
                worker: true,
                complete: (result) => {
                    const { data, errors, meta } = result;
                    const records
                    = data.filter((row) => /^\d{3}/.test(row[0]))
                    .map((row) => {
                        const newRow = row.slice(0, 5);
                        newRow.push(`${row[5]}${row[6]}`); // 기본예상운행시간 시+분
                        newRow.push(`${row[7]}${row[8]}`); // 출발요청시간 시+분
                        newRow.push(`${row[9]}${row[10]}`); // 도착요청시간 시+분

                        return newRow;
                    });
                    setCsvData({data: records, errors, meta});
                }
            })
        }
    }, [text, readString]);

    useEffect(()=>{
        if(open) {
            const dscId = sessionInfo.user.dscIdAs;
            const {records, countError} = checkAndMap(dscId, csvData.data);
            setTableData(records);
            setMsgDone('');
            setErrorCount(countError);
            setChanged(true);
        }
    }, [csvData, sessionInfo, open]);

    const onCompletedUpload = (data, option) => {
        if(data.arriveInfoUpload.ok) {
            // reflect the result to the table.(ok, message, list {ok, message, arriveId})
            const newTableData = [];
            for(const prev of tableData) {
                const found = data.arriveInfoUpload.list.find((item)=>`${prev.dcCd}${prev.storeId}` === item.arriveId);
                if(found) {
                    prev.ok = found.ok;
                    prev.message = found.message;
                    newTableData.push(prev);
                    if(found.ok) wellUploadCountRef.current++;
                }
                else newTableData.push(prev);
            }
            setTableData(newTableData);
            setChanged(false); // 다시 올리지 않도록.
            enqueueSnackbar("도착정보 업로드 완료", {variant: 'success'});
            setMsgDone('[업로드 완료] ');
            // onClose();
        }
        else addAlert({title: "도착정보 업로드", message: [data.arriveInfoUpload.message]});
    };

    const onClickUpload = () => {
        // 아래 filter에서 "row &&" 빼면 오류 발생함. row가 널이거나 undefined인 경우가 없는데도 발생함. 헉!
        const toUpload = tableData.filter((row)=>{
            if(row && row.ok) return true;
            else return false;
        }).map((row)=>InputUtil.getDataToSubmit(row, uploadMap, false));
        const param = {variables: {arriveInfoList: toUpload}};

        uploadArriveInfo(param);
    };

    const onClickFindFile = () => {
        if(fileInputRef.current) {
            fileInputRef.current.value='';
            fileInputRef.current.click();
        }
    };

    const onCsvFileRead = (event) => {
        const content = event.target.result;
        setText(content);
    };

    const onChooseFile = (event) => {
        if(event.target.files.length > 0) {
            const file = event.target.files[0];
    
            if (file) {
                const reader = new FileReader();
                reader.onload = onCsvFileRead;
                if(useEucKr) reader.readAsText(file, 'euc-kr');
                else reader.readAsText(file); // utf-8
            }
        }
    };

    const alertErrorData = (row) => {
        const lines = row.message.split("\n").filter((line)=>line.length > 0);
        addAlert({title: `도착정보 오류(${row.dcCd}-${row.storeId})`, message: lines});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case 'error': alertErrorData(row); break;
            default: break;
        }
    };

    const canUpload = checkAuth(authTableTree.tree.pr_arrive_info_upload);

    const tools = [
        {id:'error', icon: <NotificationImportantIcon/>, color: AppStyle.mui.error, where: (row) => !row.ok}
    ];

    return(
        <DraggableEditor open={open} maxWidth='md'
            startIcon={<CloudUploadIcon size={AppWord.LARGE} />}
            title="도착정보 업로드"
        >
            <DialogContent style={{position: 'relative', minHeight:'50vh', minWidth:600, width:'50vw'}}>
                <Box style={{position:'absolute', left:5, top:0, right:5, bottom:3, display:'flex', flexDirection: 'column'}}>
                    <HeaderLine>
                        <div style={{flexGrow:1, display:'flex', alignItems:'center'}}>
                        {msgDone && <ThumbUpAltIcon />}
                        {
                            tableData.length < 1 || !changed ?
                            <span>{msgDone}데이터 파일(CSV)을 선택해 주세요</span>
                            :
                            errorCount === 0 ?
                            <span>모든 데이터가 정상입니다. 업로드 버튼을 눌러주세요</span>
                            :
                            errorCount === tableData.length ?
                            <span>모든 데이터가 오류입니다. 수정해 주세요 (오류는 각 줄 끝 아이콘 눌러 확인)</span>
                            :
                            <span>오류가 없는 데이터만 올리려면 업로드 버튼을 눌러주세요 (오류는 각 줄 끝 아이콘 눌러 확인)</span>
                        }
                        </div>
                        <div style={{fontSize:'0.8rem'}}>나의 DC코드: {sessionInfo.user.dscIdAs}</div>
                    </HeaderLine>
                    <HeaderLine style={{backgroundColor:'#def'}}>
                        이 테이블은 CSV 파일의 시간, 분을 합쳐서 표시합니다. CSV에서는 시간과 분을 분리해서 입력해 주세요.
                    </HeaderLine>
                    <FlexyTable
                        name={tableProps.tableId}
                        tableProps={tableProps}
                        records={tableData}
                        tools={tools}
                        onClickTool={onClickToolOnRow}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <input
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={onChooseFile}
                />
                {
                    fileInputRef.current && canUpload &&
                    <>
                    <VzmCheckbox
                        dict={{
                            id: 'useEucKr', label: '업로드 파일 완성형한글로 읽기',
                        }}
                        value={useEucKr}
                        onChange={(id,value,err) => setUseEucKr(value)}
                    />
                    <Button
                        color={AppStyle.mui.secondary}
                        style={{marginRight:10}}
                        onClick={onClickFindFile}
                        startIcon={<FindInPageIcon />}
                        variant={AppStyle.mui.contained}
                    >
                        다른 파일 선택
                    </Button>
                    <span
                        style={{display:'inline-block', margin:'0px 20px 0px 10px', color:'#999'}}
                    >|</span>
                    </>
                }
                {
                    canUpload &&
                    <Button
                        color={AppStyle.mui.primary}
                        style={{marginRight:10}}
                        startIcon={<CloudUploadIcon />}
                        onClick={onClickUpload}
                        variant={AppStyle.mui.contained}
                        disabled={
                            (errorCount > 0 && errorCount === tableData.length) || !changed
                            || responseUpload.loading
                        }
                    >
                        데이터 업로드
                    </Button>
                }
                <Button onClick={()=>onClose(wellUploadCountRef.current)} color={AppStyle.mui.primary}
                    variant={AppStyle.mui.contained}
                    startIcon={<DoneIcon />}
                    disabled={responseUpload.loading}
                >
                    닫기
                </Button>
            </DialogActions>
        </DraggableEditor>
    );
}